const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?$/;

/** Calendar date in local timezone (`YYYY-MM-DD`). */
export function isoDate(ts: number): string {
  const d = new Date(ts);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function isDateOnly(value: string): boolean {
  return DATE_ONLY.test(value);
}

/** Start of local calendar day as `datetime-local` value. */
export function startOfDayDatetimeLocal(ts: number): string {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return toDatetimeLocalValue(d);
}

/** End of local calendar day as `datetime-local` value (`23:59`). */
export function endOfDayDatetimeLocal(ts: number): string {
  const d = new Date(ts);
  d.setHours(23, 59, 0, 0);
  return toDatetimeLocalValue(d);
}

/** Now, rounded down to the minute, for `datetime-local`. */
export function nowDatetimeLocal(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  return toDatetimeLocalValue(d);
}

/**
 * Local Monday 00:00 of the week containing `ts` (Mon–Sun weeks, matching Full weeks charts).
 * Sunday belongs to the week that started the previous Monday.
 */
export function startOfLocalWeekMonday(ts: number = Date.now()): Date {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0 = Sun … 6 = Sat
  const delta = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + delta);
  return d;
}

/**
 * Four most recently completed Monday–Sunday weeks (excludes the current incomplete week).
 * e.g. on a Thursday: from = Monday 28 days before this week's Monday, to = last Sunday 23:59.
 */
export function prevFourCompleteWeeksRange(now: number = Date.now()): {
  from: string;
  to: string;
} {
  const thisMonday = startOfLocalWeekMonday(now);
  const fromMonday = new Date(thisMonday);
  fromMonday.setDate(fromMonday.getDate() - 28);
  const lastSunday = new Date(thisMonday);
  lastSunday.setDate(lastSunday.getDate() - 1);
  return {
    from: startOfDayDatetimeLocal(fromMonday.getTime()),
    to: endOfDayDatetimeLocal(lastSunday.getTime()),
  };
}

export function toDatetimeLocalValue(input: Date | string): string {
  const d = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

/**
 * Normalize URL/filter state. Offset-bearing timestamps stay exact ISO instants;
 * offset-free picker input stays local. Display conversion must not replace stored instants.
 * Date-only `to` becomes end-of-day; date-only `from` becomes start-of-day.
 */
export function normalizeFilterDateTime(
  value: string | null,
  fallback: string,
  role: "from" | "to",
): string {
  if (!value) return fallback;
  if (DATETIME_LOCAL.test(value)) return value;
  if (DATE_ONLY.test(value)) {
    const ts = new Date(`${value}T00:00:00`).getTime();
    return role === "to" ? endOfDayDatetimeLocal(ts) : startOfDayDatetimeLocal(ts);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return parsed.toISOString();
}

/** Display-only local wall time for a date picker; never use it as the canonical stored bound. */
export function filterDateTimeDisplayValue(value: string): string {
  if (DATETIME_LOCAL.test(value) || DATE_ONLY.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const local = toDatetimeLocalValue(parsed);
  if (parsed.getSeconds() || parsed.getMilliseconds()) {
    return `${local}:${String(parsed.getSeconds()).padStart(2, "0")}.${String(parsed.getMilliseconds()).padStart(3, "0")}`;
  }
  return local;
}

/** Query param for analytics API (`YYYY-MM-DD` or ISO timestamp). */
export function filterRangeToApiParam(value: string): string {
  if (DATE_ONLY.test(value)) return value;
  if (DATETIME_LOCAL.test(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  return value;
}
